import { useState } from "react";
import { Plus, Pencil, Trash2, CalendarRange, LayoutTemplate } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { computeMetrics, fmtMoney, uid } from "@/lib/finance";
import { PageHeader, SectionCard, EmptyState, AddButton } from "@/components/shared";
import { FormModal, TextField, NumberField } from "@/components/forms";
import { AllocationDonut } from "@/components/Charts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const BUCKETS = ["needs", "life", "wealth", "debt", "giving"];

const TEMPLATES = [
  { name: "Balanced (50/20/20/5/5)", desc: "A steady, all-round plan", needs: 50, life: 20, wealth: 20, debt: 5, giving: 5 },
  { name: "Aggressive saver", desc: "Maximize wealth building", needs: 50, life: 15, wealth: 30, debt: 3, giving: 2 },
  { name: "Debt crusher", desc: "Attack debt hard", needs: 55, life: 10, wealth: 5, debt: 28, giving: 2 },
  { name: "FIRE sprint", desc: "Extreme savings for early retirement", needs: 45, life: 10, wealth: 40, debt: 3, giving: 2 },
  { name: "Generous giver", desc: "Lead with generosity", needs: 50, life: 15, wealth: 20, debt: 5, giving: 10 },
  { name: "Lean living", desc: "Trim wants, keep flexibility", needs: 60, life: 12, wealth: 18, debt: 5, giving: 5 },
  { name: "Comfort & lifestyle", desc: "Enjoy more of today", needs: 50, life: 30, wealth: 12, debt: 5, giving: 3 },
  { name: "New parent", desc: "Buffer for a growing family", needs: 62, life: 13, wealth: 15, debt: 5, giving: 5 },
];

export default function LifePlan() {
  const { state, update, loading } = useFinancialState();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [selected, setSelected] = useState(0);
  const [tplOpen, setTplOpen] = useState(false);
  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const plans = state.lifePlan || [];
  const m = computeMetrics(state);
  const income = m.income || 0;
  const active = plans[selected] || plans[0];

  const openNew = () => { setForm({ id: uid(), month: new Date().toISOString().slice(0, 7), seasonName: "", needs: 50, life: 20, wealth: 20, debt: 5, giving: 5 }); setOpen(true); };

  const applyTemplate = (tpl) => {
    const season = { id: uid(), month: new Date().toISOString().slice(0, 7), seasonName: tpl.name,
      needs: tpl.needs, life: tpl.life, wealth: tpl.wealth, debt: tpl.debt, giving: tpl.giving };
    update((prev) => ({ ...prev, lifePlan: [...(prev.lifePlan || []), season] }), `Added season: ${tpl.name}`);
    setTplOpen(false);
    setSelected((state.lifePlan || []).length);
    toast.success(`Added "${tpl.name}" — tweak it anytime`);
  };

  const save = () => {
    if (!form.seasonName) return toast.error("Season name is required");
    const total = BUCKETS.reduce((s, k) => s + Number(form[k] || 0), 0);
    if (total !== 100) return toast.error(`Allocations must total 100% (currently ${total}%)`);
    const p = { ...form };
    BUCKETS.forEach((k) => (p[k] = Number(p[k] || 0)));
    update((prev) => {
      const exists = (prev.lifePlan || []).some((x) => x.id === p.id);
      return { ...prev, lifePlan: exists ? prev.lifePlan.map((x) => (x.id === p.id ? p : x)) : [...(prev.lifePlan || []), p] };
    }, `Saved life plan: ${p.seasonName}`);
    setOpen(false); toast.success("Life plan saved");
  };
  const remove = (id) => { update((prev) => ({ ...prev, lifePlan: prev.lifePlan.filter((x) => x.id !== id) }), "Deleted life plan"); setSelected(0); };

  const donutData = active ? BUCKETS.map((k) => ({ name: k[0].toUpperCase() + k.slice(1), value: active[k] })) : [];

  return (
    <div>
      <PageHeader title="Life plan" subtitle="Design monthly financial seasons and allocate every dollar with intention." testid="lifeplan-header"
        actions={<>
          <AddButton variant="secondary" onClick={() => setTplOpen(true)} testid="choose-template-button"><LayoutTemplate className="h-4 w-4 mr-1.5" />Choose a template</AddButton>
          <AddButton onClick={openNew} testid="add-season-button"><Plus className="h-4 w-4 mr-1.5" />Add season</AddButton>
        </>} />

      {plans.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-5 space-y-2">
            {plans.map((p, i) => (
              <div key={p.id} role="button" tabIndex={0} onClick={() => setSelected(i)} data-testid={`season-${p.id}`}
                className={`w-full text-left cursor-pointer rounded-[12px] border px-4 py-3 transition-colors ${i === selected ? "border-[color:rgba(31,115,85,0.35)] bg-[color:rgba(31,115,85,0.06)]" : "border-[color:var(--border-subtle)] bg-[var(--surface-1)] hover:border-[color:var(--border-strong)]"}`}>
                <div className="flex items-center justify-between">
                  <div><div className="font-medium text-[var(--text)]">{p.seasonName}</div><div className="text-[11px] text-[var(--text-faint)]">{p.month}</div></div>
                  <div className="flex">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setForm({ ...p }); setOpen(true); }} aria-label="Edit"><Pencil className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)]" onClick={(e) => { e.stopPropagation(); remove(p.id); }} aria-label="Delete"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-7">
            <SectionCard title={active?.seasonName} subtitle={`Allocating ${fmtMoney(income)}/mo income`} testid="season-detail">
              <AllocationDonut data={donutData} height={220} />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                {BUCKETS.map((k) => (
                  <div key={k} className="rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-[var(--text-muted)] capitalize">{k}</div>
                    <div className="font-serif-display text-lg tabular-nums">{active[k]}%</div>
                    <div className="text-[11px] text-[var(--text-faint)]">{fmtMoney((income * active[k]) / 100)}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        </div>
      ) : <SectionCard><EmptyState icon={CalendarRange} title="No seasons yet" message="Start from a ready-made template or build your own from scratch." action={<div className="flex gap-2"><AddButton onClick={() => setTplOpen(true)}><LayoutTemplate className="h-4 w-4 mr-1.5" />Choose a template</AddButton><AddButton variant="secondary" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Add season</AddButton></div>} /></SectionCard>}

      {/* Template picker */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-[var(--surface-2)] border border-[color:var(--border-subtle)] max-w-lg" data-testid="template-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl text-[var(--text)]">Season templates</DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">Pick a pre-filled plan to start — you can fine-tune it after.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-auto py-1">
            {TEMPLATES.map((t) => (
              <button key={t.name} onClick={() => applyTemplate(t)} data-testid={`template-${t.name.split(" ")[0]}`}
                className="w-full text-left rounded-[12px] border border-[color:var(--border-subtle)] bg-white px-4 py-3 hover:border-[color:rgba(31,115,85,0.4)] hover:bg-[color:rgba(31,115,85,0.05)] transition-colors">
                <div className="flex items-center justify-between">
                  <div><div className="font-medium text-[var(--text)]">{t.name}</div><div className="text-[11px] text-[var(--text-faint)]">{t.desc}</div></div>
                  <div className="flex gap-1">
                    {BUCKETS.map((k, i) => (
                      <span key={k} title={k} className="h-6 rounded-sm" style={{ width: 6, backgroundColor: ["#1f7355", "rgba(31,115,85,0.5)", "#c68b3c", "rgba(198,139,60,0.5)", "rgba(23,35,31,0.25)"][i], height: `${8 + t[k] * 0.3}px` }} />
                    ))}
                  </div>
                </div>
                <div className="text-[11px] text-[var(--text-muted)] mt-1.5">Needs {t.needs}% · Life {t.life}% · Wealth {t.wealth}% · Debt {t.debt}% · Giving {t.giving}%</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {form && (
        <FormModal open={open} onOpenChange={setOpen} title={plans.some((x) => x.id === form.id) ? "Edit season" : "Add season"} onSubmit={save} testid="season-modal" description="Allocations must total 100%.">
          <TextField label="Season name" value={form.seasonName} onChange={(e) => setForm({ ...form, seasonName: e.target.value })} testid="season-name-input" placeholder="Wealth-building sprint" />
          <TextField label="Month" type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} />
          {BUCKETS.map((k) => (
            <NumberField key={k} label={`${k[0].toUpperCase() + k.slice(1)} %`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          ))}
        </FormModal>
      )}
    </div>
  );
}
